import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FatigueAssessmentForm, FatigueAssessmentRiskEnum, FatigueAssessmentTaskEnum } from 'shared/models/api/models';
import { fatigueAssessmentFormUrl } from 'components/ui/FatigueAssessmentRules';
import { formatDateTime } from "shared/models/mapping/mapping";
import { FatigueAssessmentView } from './FatigueAssessmentView';
import { Tabs } from './FatigueAssessmentDetails';
import { fatigueAssessmentDashboardPathRoute, dashboardPending, dashboardAction, dashboardRevisit, dashboardCompleted, dashboardOverview } from './FatigueAssessmentRoutes';

import {
    FiberManualRecord,
    InfoOutlined as InfoIcon,
    Reply as ActionIcon,
    AssignmentTurnedInOutlined as StatusComplete,
    QueryBuilder as StatusPending
} from '@mui/icons-material';


interface CardProps {
    assessment: FatigueAssessmentForm
    adminView?: boolean,
    currentView: FatigueAssessmentView
}

const CardRow = (props: CardProps) => {
    const [assessment] = useState(props.assessment);
    const navigate = useNavigate();

    const shiftStartTimeFormatted = () => {
        var date = assessment.shiftStartTime || new Date();
        return formatDateTime(date);
    }

    const isActionableOverdue = (shiftStartTime?: Date): boolean => {
        if (typeof (shiftStartTime) === 'string') {
            shiftStartTime = new Date(shiftStartTime);
        }
        let wellOverdueThreshold = 30 * 60000; //30 minutes
        if (shiftStartTime)
            return shiftStartTime.getTime() < Date.now() - wellOverdueThreshold

        return false;
    }

    const displayDue = (shiftStartTime?: Date) => {

        if (typeof (shiftStartTime) === 'string') {
            shiftStartTime = new Date(shiftStartTime);
        }

        if (shiftStartTime === undefined) {
            return (<></>);
        }

        let dueState = "due";
        let dueMessage = "Due";

        let slightOverdueThreshold = 15 * 60000; //15 minutes


        if (isActionableOverdue(shiftStartTime)) {
            dueState = "overdue";
            dueMessage = "Overdue >30 min";
        }
        else if (shiftStartTime.getTime() < Date.now() - slightOverdueThreshold) {
            dueState = "overdue";
            dueMessage = "Overdue >15 min";
        }
        else if (shiftStartTime.getTime() < Date.now()) {
            dueState = "over-slight";
            dueMessage = "Overdue <15 min";
        }

        return (
            <>
                <div className={`assessment-status assessment-${ dueState }`}><FiberManualRecord /></div>
                <div className="overflow-hidden">{dueMessage}</div>
            </>
        );

    }

    const displayRisk = (risk?: FatigueAssessmentRiskEnum) => {

        if (risk === undefined || risk === FatigueAssessmentRiskEnum.NA) {
            return (<></>);
        }

        return (
            <div className={`fa-risk-${ (FatigueAssessmentRiskEnum[risk]).toLowerCase() }`}>
                <FiberManualRecord />
                <div>{FatigueAssessmentRiskEnum[risk]}</div>
            </div>
        );
    }

    const displayTask = (task?: FatigueAssessmentTaskEnum) => {
        if (task === FatigueAssessmentTaskEnum.NoAction) {
            return (
                <div className="fa-task-action">No action</div>
            );
        }

        else if (task === FatigueAssessmentTaskEnum.ToAction) {
            return (
                <div className="fa-task-action fa-task-to-action">To action</div>
            );
        }

        else if (task === FatigueAssessmentTaskEnum.ToRevisit) {
            return (
                <div className="fa-task-action fa-task-to-revisit">To revisit</div>
            );
        }

        return (<></>);
    }

    const pages: { [id: number]: string; } = {};
    pages[FatigueAssessmentView.Pending] = dashboardPending;
    pages[FatigueAssessmentView.ToAction] = dashboardAction;
    pages[FatigueAssessmentView.ToRevisit] = dashboardRevisit;
    pages[FatigueAssessmentView.Completed] = dashboardCompleted;
    pages[FatigueAssessmentView.Overview] = dashboardOverview;

    const getActionPath = (id: number, tab: string) => {
        let url = `${ fatigueAssessmentDashboardPathRoute.path }/${ pages[props.currentView] }/${ id }/${ tab }`;
        return url;
    }

    const displayFatigueAssessmentDetails = (event: any, tab?: string) => {
        event.stopPropagation();

        if (props.adminView !== true || props.currentView == FatigueAssessmentView.Overview) {
            return;
        }

        if (!tab) {
            tab = (props.currentView == FatigueAssessmentView.Pending) ? Tabs.notes : Tabs.actions;
        }

        const path = getActionPath(assessment.id, tab);
        navigate(path);
    }

    return (
        <div
            className={`row align-items-center assessment-card assessment-card-row ${ props.adminView ? "" : "assessment-card-myform" }`}
            onClick={(event) => displayFatigueAssessmentDetails(event)} >

            {props.adminView === true &&
                <>
                    <div className="fa-col-status d-none d-xl-inline-block">
                        {assessment.assessmentDate ?
                            <div className="fa-status-complete"><StatusComplete /></div>
                            :
                            <div className="fa-status-pending"><StatusPending /></div>
                        }
                    </div>
                </>
            }

            <div className="fa-col-due">
                {!assessment.assessmentDate &&
                    <>
                        {displayDue(assessment.shiftStartTime)}
                    </>
                }
            </div>

            <div className="col">
                <span className="font-weight-bold">{assessment.user?.fullname}</span>
                <div className="d-xl-none fa-details-narrow">
                    <div><span className="assessment-card-main-label">Location: </span>{assessment.signOnLocation?.name}</div>
                    <div><span className="assessment-card-main-label">Shift start time: </span>{`${ shiftStartTimeFormatted() }`}</div>
                    {
                        (props.currentView === FatigueAssessmentView.Overview) &&
                        <div><span className="assessment-card-main-label">Supervisor: </span>{assessment.supervisor?.fullname}</div>
                    }
                </div>
            </div>

            <div className="fa-col-location d-none d-xl-inline-block">{assessment.signOnLocation?.name}</div>

            <div className="fa-col-shift-start-time d-none d-xl-inline-block"><div className="w-75">{`${ shiftStartTimeFormatted() }`}</div></div>

            {
                (props.currentView === FatigueAssessmentView.Overview || props.currentView === FatigueAssessmentView.ToAction) &&
                <div className="col d-none d-xl-inline-block">{assessment.supervisor?.fullname}</div>
            }

            {props.adminView === true &&
                <>
                    <div className="fa-col-risk d-none d-sm-inline-block">{displayRisk(assessment.risk)}</div>

                    <div className="fa-col-task d-none d-lg-inline-block">{displayTask(isActionableOverdue(assessment.shiftStartTime) && !(assessment.assessmentDate) ? FatigueAssessmentTaskEnum.ToAction : assessment.task)}</div>

                    {
                        (props.currentView !== FatigueAssessmentView.Overview) &&
                        <div
                            className="fa-col-action fa-action"
                            onClick={(event) => displayFatigueAssessmentDetails(event)}>
                            <div className="d-flex align-items-center h-100">
                                <div className="w-100"><ActionIcon /></div>
                            </div>
                        </div>
                    }

                    <div
                        className="fa-col-info fa-info align-items-center"
                        onClick={(event) => displayFatigueAssessmentDetails(event, Tabs.info)}>
                        <div className="d-flex align-items-center h-100">
                            <div className="w-100"><InfoIcon /></div>
                        </div>
                    </div>
                </>
            }

        </div>
    );
}


const FatigueAssessmentCard = (props: CardProps) => {
    const cardUrl = fatigueAssessmentFormUrl(props.assessment.id);

    return (
        <>
            {props.adminView !== true ?
                <Link to={cardUrl} className="text-decoration-none">
                    <CardRow {...props} />
                </Link>
                :
                <CardRow {...props} />
            }
        </>
    );
}

export default FatigueAssessmentCard;
